import React, {useEffect, useState} from "react";
import {
    Box,
    Flex,
    Button,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    useColorModeValue,
    IconButton, Text, useToast,
    keyframes, useBreakpointValue
} from '@chakra-ui/react';
import { Chat } from './Chat';
import { Highlights } from './Highlights';
import { useDispatch, useSelector } from "react-redux";
import {
    addMessageInDocument,
    setAskContent, setAskImageContent, setCurrentChatInput,
    setFollowUpContent, setWhichTab, toggleIsAskImageVisibleFalse,
    toggleIsAskVisibleFalse,
    toggleIsChatVisible, toggleIsFollowUpVisibleFalse
} from "../../redux/reducers";
import { v4 as uuidv4 } from 'uuid';
import {useAuth} from "../../redux/AuthProvider";
import {AiOutlineZoomOut} from "react-icons/ai";
import {FiChevronUp, FiMinimize2} from "react-icons/fi";
import lightTheme from "../../utils/styles";
import {CustomIconButton, CustomIconButtonWhite} from "../buttons/CustomIconButton";
import {Generation} from "./Generation";
import {useTranslation} from "react-i18next";
import {ChatVideo} from "./ChatVideo";
import {getEditableTranscriptVideo} from "../../utils/pdf-page-helpers";
import {useServerIP} from "../../redux/ServerIPContext";
import html2pdf from "html2pdf.js";

// handleChatResponseNoSelection = async (type, pageNumber, sessionToken, socketID, question)
export function SidebarVideo({ stopClaudeResponse, highlights, documentHere, sessionToken, width, handleChatResponseNoSelection, socketID,
                            handleChatResponseSelection, handleChatFollowUp, deleteHighlight, updateHighlightInDoc }) {
    const dispatch = useDispatch();
    const documents = useSelector(state => state.documents.savedDocuments);
    const isAskVisible = useSelector(state => state.settings.isAskVisible);
    const isAskImageVisible = useSelector(state => state.settings.isAskImageVisible);
    const isFollowUpVisible = useSelector(state => state.settings.isFollowUpVisible);
    const askContent = useSelector(state => state.settings.askContent);
    const askImageContent = useSelector(state => state.settings.askImageContent);
    const followUpContent = useSelector(state => state.settings.followUpContent);
    const whichTab = useSelector(state => state.settings.whichTab)
    const currentChatInput = useSelector(state => state.settings.currentChatInput);
    const {t} = useTranslation();
    const { serverIPs, setServerIPs, isDev } = useServerIP();
    const toast = useToast();
    const breakpoint = useBreakpointValue({ base: 'base', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' });
    const isMobile = breakpoint === 'base';

    // type, message_responding_to, follow_up_question, sessionToken, socketID
    const handleNewMessage = (chatInput) => {
        if (isAskVisible) {
            handleChatResponseSelection('ask_selection', askContent, sessionToken, socketID, chatInput);
            dispatch(toggleIsAskVisibleFalse());
            dispatch(setAskContent(null));
        }
        else if (isAskImageVisible) {
            handleChatResponseSelection('ask_image', askImageContent, sessionToken, socketID, chatInput);
            dispatch(toggleIsAskImageVisibleFalse());
            dispatch(setAskImageContent(null));
        }
        else if (isFollowUpVisible) {
            handleChatFollowUp("follow_up", followUpContent.response, chatInput, sessionToken, socketID);
            dispatch(toggleIsFollowUpVisibleFalse());
            dispatch(setFollowUpContent(null));
        } else {
            handleChatResponseNoSelection('general_question', null, sessionToken, socketID, chatInput);
        }
        dispatch(setCurrentChatInput(''));
    };

    const slide = keyframes`
        from {
            margin-left: 0%;
        }
        to {
            margin-left: 50%;
        }
    `;

    const [processedTranscript, setProcessedTranscript] = useState([]);
    const [isExportingPdf, setIsExportingPdf] = useState(false);

    const handleExportPdf = () => {
        const element = document.getElementById("transcript-container");
        if (!element) return;

        // html2pdf options: margins, page size, filename, quality
        const opts = {
            margin:       [10, 10, 10, 10],                    // [top, left, bottom, right] in mm
            filename:     `transcript-${documentHere.id}.pdf`,
            image:        { type: 'jpeg', quality: 0.98 },
            html2canvas:  { scale: 2, logging: false },
            jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf()
            .set(opts)
            .from(element)
            .save();
    };

    const handleTextExport = () => {
        try {
            // Create text content from transcript
            let textContent = `Transcript - ${documentHere.id}\n`;
            textContent += `Generated on: ${new Date().toLocaleDateString()}\n`;
            textContent += `Total segments: ${processedTranscript.length}\n\n`;
            textContent += "=".repeat(50) + "\n\n";
            
            processedTranscript.forEach((line, i) => {
                textContent += `[${line.timeLabel}] ${line.speaker}\n`;
                textContent += `${line.text}\n\n`;
            });

            // Create and download file
            const blob = new Blob([textContent], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `transcript-${documentHere.id}.txt`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({
                title: "Text Export Successful",
                description: `Transcript exported as text file with ${processedTranscript.length} segments.`,
                status: "success",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            console.error("Text export error:", error);
            toast({
                title: "Text Export Failed",
                description: "Could not export transcript as text file.",
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        let isCancelled = false;

        const fetchTranscript = async () => {
            const resp = await getEditableTranscriptVideo(
                documentHere.id,
                localStorage.getItem("userID"),
                serverIPs.SERVER_IP_VIDEO
            );

            if (isCancelled) return;

            if (resp && resp.status === 200) {
                // —— process segments into [ {timeLabel, speaker, text}, … ]
                const speakerMap = {};
                resp.speaker_predictions.forEach(sp => {
                    speakerMap[sp.speaker_tag] = sp.speaker_name;
                });

                const processed = resp.transcript_data.segments.map(seg => {
                    const mins = Math.floor(seg.start / 60);
                    const secs = Math.floor(seg.start % 60)
                        .toString()
                        .padStart(2, "0");
                    const timeLabel = `${mins}:${secs}`;
                    const speakerName = speakerMap[seg.speaker] || seg.speaker;
                    return {
                        timeLabel,
                        speaker: speakerName,
                        text: seg.text.trim(),
                    };
                });

                setProcessedTranscript(processed);
            }
            else if (resp && resp.status === 400) {
                // not ready – retry in 5s
                setTimeout(fetchTranscript, 5000);
            }
            else {
                // fail silently for now
            }
        };

        fetchTranscript();
        return () => { isCancelled = true; };
    }, []);

    return (
        <Box width={`${width}%`} height="100%" display="flex" flexDirection="column">
            <Flex height="3rem" backgroundColor={lightTheme.colors.white} alignItems="center"
                  justifyContent="space-between" px={'1rem'} borderBottomColor={lightTheme.colors.lightGray}
                  borderBottomWidth={"0.5px"}>



                <Flex alignItems={"center"}>
                    <Button
                        borderRadius={0}
                        backgroundColor={lightTheme.colors.white}
                        onClick={() => dispatch(setWhichTab(1))}
                        borderBottom={whichTab === 1 ? "2px solid blue" : "none"}
                        _hover={{ backgroundColor: lightTheme.colors.white }}
                    >
                        {t("Chat")}
                    </Button>
                    {/*<Button*/}
                    {/*    borderRadius={0}*/}
                    {/*    backgroundColor={lightTheme.colors.white}*/}
                    {/*    onClick={() => dispatch(setWhichTab(2))}*/}
                    {/*    borderBottom={whichTab === 2 ? "2px solid blue" : "none"}*/}
                    {/*    _hover={{ backgroundColor: lightTheme.colors.white }}*/}
                    {/*>*/}
                    {/*    <Flex align="center">*/}
                    {/*        <Box>Generate</Box>*/}
                    {/*        <Box*/}
                    {/*            width="2.9rem"*/}
                    {/*            height="1.5rem"*/}
                    {/*            display={"flex"}*/}
                    {/*            justifyContent={"center"}*/}
                    {/*            alignItems={"center"}*/}
                    {/*            borderRadius={'6px'}*/}
                    {/*            backgroundColor="black"*/}
                    {/*            marginLeft="0.7rem">*/}
                    {/*            <Text color={'white'} fontSize={'xs'}>NEW</Text>*/}
                    {/*        </Box>*/}
                    {/*    </Flex>*/}
                    {/*</Button>*/}
                    {processedTranscript.length !== 0 ?
                        <Button
                            borderRadius={0}
                            backgroundColor={lightTheme.colors.white}
                            onClick={() => dispatch(setWhichTab(3))}
                            borderBottom={whichTab === 3 ? "2px solid blue" : "none"}
                            _hover={{ backgroundColor: lightTheme.colors.white }}
                        >
                            {t("Transcript")}
                        </Button> : null}
                    {processedTranscript.length !== 0 && whichTab === 3 ?
                        <Menu>
                            <MenuButton 
                                as={Button} 
                                size="sm" 
                                variant="outline" 
                                ml={6}
                                isLoading={isExportingPdf}
                                loadingText="Exporting..."
                                isDisabled={isExportingPdf}
                            >
                                Export Transcript
                            </MenuButton>
                            <MenuList>
                                <MenuItem onClick={handleExportPdf}>
                                    Export as PDF
                                </MenuItem>
                                <MenuItem onClick={handleTextExport}>
                                    Export as Text
                                </MenuItem>
                            </MenuList>
                        </Menu> : null}
                </Flex>







                {isMobile ?
                    <Button
                        borderRadius={10}
                        fontSize={14}
                        px={3}
                        py={0}
                        mt={-2}
                        color={'white'}
                        bgGradient={'linear(to-r, red.400,pink.400)'}
                        onClick={() => {
                            dispatch(toggleIsChatVisible())
                        }}
                    >
                        Show File
                    </Button> :
                    <CustomIconButtonWhite
                        icon={FiMinimize2}
                        size={19}
                        ariaLabel="Minimize Chat"
                        onClick={() => {
                            dispatch(toggleIsChatVisible());
                        }}
                    />}
            </Flex>

            {whichTab === 1 ?
                <ChatVideo theDocument={documentHere} handleNewMessage={handleNewMessage} handleChatResponseNoSelection={handleChatResponseNoSelection}
                      sessionToken={sessionToken} socketID={socketID} stopClaudeResponse={stopClaudeResponse}/>
                :
                whichTab === 2 ?
                    <Generation theDocument={documentHere} handleNewMessage={(chatInput) => {console.log("BADA BING", chatInput)}}
                                sessionToken={sessionToken} socketID={socketID} stopClaudeResponse={stopClaudeResponse}/>
                    :
                    <Box
                        id="transcript-container"
                        flex="1"
                        overflowY="auto"
                        bg="white"
                        p={4}
                        borderBottom={`1px solid ${lightTheme.colors.lightGray}`}
                    >
                        {processedTranscript.map((line, i) => (
                            <Flex key={i} mb={3}>
                                <Box w="30%" pr={2}>
                                    <Text fontSize="sm" fontWeight="bold">
                                        {line.timeLabel} {line.speaker}
                                    </Text>
                                </Box>
                                <Box w="70%">
                                    <Text fontSize="sm">{line.text}</Text>
                                </Box>
                            </Flex>
                        ))}
                    </Box>
            }
        </Box>
    );
}
